import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { usePostHog } from 'posthog-js/react';
import { REDESIGN_COLORS } from '../../styles/colors';

const TABS = [
  { key: 'home', label: 'Home', icon: 'home', route: '/home' },
  { key: 'favorites', label: 'Favorites', icon: 'heart', route: '/favorites' },
  { key: 'negotiations', label: 'Negotiations', icon: 'handshake', route: '/negotiations' },
  { key: 'cars', label: 'Cars', icon: 'car-multiple', route: '/cars' },
  { key: 'settings', label: 'Settings', icon: 'cog', route: '/settings' },
];

const THEME = {
  dark: {
    background: REDESIGN_COLORS.bottomBar,
    active: REDESIGN_COLORS.textPrimary,
    inactive: REDESIGN_COLORS.navInactive,
    borderTop: undefined,
  },
  light: {
    background: '#FFFFFF',
    active: '#0E0E0E',
    inactive: 'rgba(14,14,14,0.4)',
    borderTop: 'rgba(14,14,14,0.08)',
  },
};

/**
 * BottomTabBar - Fixed bottom tab navigation for mobile.
 *
 * @param {Object} props
 * @param {string} props.activeTab - Currently active tab key ('home'|'favorites'|'negotiations'|'cars'|'settings')
 * @param {'dark'|'light'} [props.variant='dark'] - Color scheme
 */
const BottomTabBar = ({ activeTab, variant = 'dark' }) => {
  const router = useRouter();
  const posthog = usePostHog();
  const theme = THEME[variant] || THEME.dark;

  const handleTabPress = (tab) => {
    if (tab.key === activeTab) return;

    posthog?.capture('tab_switched', {
      tab: tab.key,
      previous_tab: activeTab,
    });

    router.push(tab.route);
  };

  return (
    <View style={[
      styles.container,
      { backgroundColor: theme.background },
      theme.borderTop && { borderTopWidth: 1, borderTopColor: theme.borderTop },
    ]}>
      <View style={styles.tabRow}>
        {TABS.map((tab) => {
          const isActive = tab.key === activeTab;
          return (
            <Pressable key={tab.key} style={styles.tab} onPress={() => handleTabPress(tab)}>
              <MaterialCommunityIcons
                name={tab.icon}
                size={24}
                color={isActive ? theme.active : theme.inactive}
              />
              <Text
                style={[
                  styles.label,
                  {
                    fontFamily: isActive ? 'Satoshi-Bold' : 'Satoshi-Medium',
                    color: isActive ? theme.active : theme.inactive,
                  },
                ]}
              >
                {tab.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingBottom: 29, // Safe area
  },
  tabRow: {
    flexDirection: 'row',
    height: 58,
    alignItems: 'center',
    justifyContent: 'space-around',
  },
  tab: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: 4,
  },
  label: {
    fontSize: 12,
  },
});

export default BottomTabBar;
